// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import type { SpoolRequest, SpoolRequestId } from '@evolvemep/api-ts';

import { getObjectPropertyValueAsString } from 'components/Autodesk/extensions/propertyHelpers';
import { buildSpoolObject, getMostFrequentLevel } from 'components/Autodesk/extensions/SpoolsExtension/helpers';
import type { BuildingSpool, RenderableSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import { isNil, isNotNil } from 'helpers/isNotNil';

export type SpoolRequestEntry = [SpoolRequestId, RenderableSpool];

/**
 * @returns a callback that can be used to load spool requests and convert them to renderable spools, or null if the
 * Autodesk extension store is not ready.
 */
export const useSpoolRequestsLoader = () => {
  const { externalIdLookup, commands } = useAutodeskExtensionStoreSnapshot('apsViewer');

  /**
   * This helper method converts a spool request into a renderable spool.
   * @returns A tuple containing the ID of the spool request and the spool request itself.
   */
  const convertRequestToRenderableSpool = useCallback(
    async (
      /** The spool request to convert. */
      request: SpoolRequest,
    ): Promise<SpoolRequestEntry> => {
      if (isNil(externalIdLookup)) {
        throw new Error('Could not convert spool request to renderable spool. External ID lookup not loaded.');
      }
      if (isNil(commands)) {
        throw new Error('Could not convert spool request to renderable spool. Commands not loaded.');
      }
      const dbIds = request.spoolItemExternalIds
        .map((externalId) => externalIdLookup[externalId])
        .filter((n) => n !== undefined);
      const bulkProperties = await commands.getBulkProperties(dbIds, {
        propFilter: [
          'name',
          'Type Name',
          'viewable_in',
          'eV_LBS_Id',
          'eV_PackageId',
          'eV_PackageStatus',
          'eVolve_UnitofMeasure',
          'eVolve_Length',
          'eVolve_Description',
          'Trade Size',
          'Quantity',
          'Manufacturer',
          'Level',
          'Reference Level',
        ],
      });
      const requestId = request.spoolRequestId;
      const spool: BuildingSpool = {
        name: request.name,
        level: undefined,
        objects: bulkProperties.map(buildSpoolObject),
      };
      spool.level = getMostFrequentLevel(spool.objects);
      // For these properties, we're currently taking the first value that we find for filtering purposes. This may be subject to change.
      for (const [spoolProp, forgeProp] of [
        ['location', 'eV_LBS_Id'],
        ['package', 'eV_PackageId'],
        ['packageStatus', 'eV_PackageStatus'],
      ] as const) {
        for (const propResult of bulkProperties) {
          const value = getObjectPropertyValueAsString(propResult, forgeProp);
          if (value !== undefined) {
            spool[spoolProp] = value;
            break;
          }
        }
      }
      return [requestId, spool];
    },
    [externalIdLookup, commands],
  );

  /** This method takes a list of spool requests as an argument and converts them to renderable spool objects */
  const load = useCallback(
    async (requests: SpoolRequest[]) =>
      await Promise.all(requests.map((request) => convertRequestToRenderableSpool(request))),
    [convertRequestToRenderableSpool],
  );

  // We only return the loader when the commands and external ID lookup are available, so that the error
  // conditions never occur.
  return isNotNil(commands) && isNotNil(externalIdLookup) ? load : null;
};
